import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, Mail, Inbox, Loader2, ChevronDown, ChevronRight, Sparkles, ArrowDownLeft, ArrowUpRight, X, Filter, Bookmark, BookmarkPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const stripHtml = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.innerText || div.textContent || "";
};

const EmailMessageRow = ({ message, query, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const [fullBody, setFullBody] = useState(null);
  const [loadingBody, setLoadingBody] = useState(false);

  const isOutbound = message.direction === "outbound";

  const handleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !fullBody) {
      setLoadingBody(true);
      try {
        const res = await axios.get(`${API}/email/activities/${message.id}/body`);
        setFullBody(res.data);
      } catch (e) {
        setFullBody({ body_html: message.snippet || "(unable to load)", body_type: "text", source: "error" });
      } finally {
        setLoadingBody(false);
      }
    }
  };

  // Highlight query in snippet
  const highlight = (text) => {
    if (!text || !query) return text;
    const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    return text.split(re).map((part, i) =>
      re.test(part) ? <mark key={i} className="bg-yellow-200">{part}</mark> : <span key={i}>{part}</span>
    );
  };

  return (
    <div className="border-l-2 border-slate-200 hover:border-[#2E7AD1] transition-colors" data-testid={`email-search-result-${message.id}`}>
      <button
        type="button"
        onClick={handleExpand}
        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2"
      >
        <div className={`mt-1 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${isOutbound ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-700"}`}>
          {isOutbound ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownLeft className="h-3.5 w-3.5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-0.5">
            <span className="font-medium text-slate-700">{isOutbound ? "To" : "From"}: {highlight(isOutbound ? message.to_email : message.from_email)}</span>
            <span className="text-slate-300">•</span>
            <span>{formatDateTime(message.date)}</span>
            {message.matched_company && (
              <>
                <span className="text-slate-300">•</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigate(`/companies/${encodeURIComponent(message.matched_company)}`); }}
                  className="text-[#2E7AD1] hover:underline"
                >
                  {message.matched_company}
                </button>
              </>
            )}
            {message.matched_lead_id && (
              <>
                <span className="text-slate-300">•</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigate(`/leads/${message.matched_lead_id}`); }}
                  className="text-[#2E7AD1] hover:underline"
                >
                  View Lead
                </button>
              </>
            )}
          </div>
          <p className="text-sm font-medium text-slate-800 truncate">{highlight(message.subject || "(No Subject)")}</p>
          {!expanded && (
            <p className="text-xs text-slate-500 truncate mt-0.5">{highlight(message.snippet || "(no preview)")}</p>
          )}
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400 mt-2" /> : <ChevronRight className="h-4 w-4 text-slate-400 mt-2" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 ml-9">
          {loadingBody ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading full message...
            </div>
          ) : fullBody?.body_type === "html" ? (
            <div
              className="prose prose-sm max-w-none text-sm text-slate-700 bg-slate-50 rounded p-3 border border-slate-100 overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: fullBody.body_html }}
            />
          ) : (
            <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-3 border border-slate-100">
              {fullBody?.body_html ? stripHtml(fullBody.body_html) : message.snippet}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function EmailSearch() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("stryde_user") || "{}");
  const [filters, setFilters] = useState({
    q: "",
    direction: "all",
    start_date: "",
    end_date: "",
    tier: "all",
    owner: "all",
    domain: "",
    company: "",
    matched_only: false,
  });
  const [results, setResults] = useState({ messages: [], total_matching: 0 });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [summarizing, setSummarizing] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);

  // Saved searches state
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveShared, setSaveShared] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSavedSearches = useCallback(async () => {
    if (!user.email) return;
    try {
      const res = await axios.get(`${API}/email/saved-searches?user_email=${encodeURIComponent(user.email)}`);
      setSavedSearches(res.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [user.email]);

  useEffect(() => {
    axios.get(`${API}/auth/users`).then(r => setUsers(r.data || [])).catch(() => {});
    fetchSavedSearches();
  }, [fetchSavedSearches]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setAiSummary(null);
    try {
      const params = new URLSearchParams();
      if (filters.q) params.append("q", filters.q);
      if (filters.direction && filters.direction !== "all") params.append("direction", filters.direction);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.tier && filters.tier !== "all") params.append("tier", filters.tier);
      if (filters.owner && filters.owner !== "all") params.append("owner", filters.owner);
      if (filters.domain) params.append("domain", filters.domain);
      if (filters.company) params.append("company", filters.company);
      if (filters.matched_only) params.append("matched_only", "true");
      params.append("group_by_thread", "false");
      params.append("limit", "100");
      const res = await axios.get(`${API}/email/search?${params.toString()}`);
      setResults(res.data || { messages: [], total_matching: 0 });
    } catch (e) {
      console.error(e);
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleAiSummary = async () => {
    if (!results.messages || results.messages.length === 0) {
      toast.error("Run a search first");
      return;
    }
    setSummarizing(true);
    try {
      const ids = results.messages.slice(0, 30).map(m => m.id);
      const res = await axios.post(`${API}/email/thread/summarize`, { activity_ids: ids });
      setAiSummary(res.data.summary);
      toast.success("Summarized top 30 results");
    } catch (e) {
      console.error(e);
      toast.error("Summary failed");
    } finally {
      setSummarizing(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      q: "", direction: "all", start_date: "", end_date: "",
      tier: "all", owner: "all", domain: "", company: "", matched_only: false,
    });
    setResults({ messages: [], total_matching: 0 });
    setAiSummary(null);
  };

  const handleSaveSearch = async () => {
    if (!saveName.trim()) {
      toast.error("Name your search");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/email/saved-searches`, {
        name: saveName.trim(),
        user_email: user.email,
        filters,
        shared: saveShared,
      });
      toast.success(`Saved "${saveName.trim()}"`);
      setSaveName("");
      setShowSaveDialog(false);
      await fetchSavedSearches();
    } catch (e) {
      console.error(e);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleLoadSavedSearch = (saved) => {
    setFilters({
      q: saved.filters.q || "",
      direction: saved.filters.direction || "all",
      start_date: saved.filters.start_date || "",
      end_date: saved.filters.end_date || "",
      tier: saved.filters.tier || "all",
      owner: saved.filters.owner || "all",
      domain: saved.filters.domain || "",
      company: saved.filters.company || "",
      matched_only: !!saved.filters.matched_only,
    });
    setTimeout(() => runSearch(), 0);
    toast.success(`Loaded "${saved.name}"`);
  };

  const handleDeleteSavedSearch = async (savedId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this saved search?")) return;
    try {
      await axios.delete(`${API}/email/saved-searches/${savedId}`);
      toast.success("Deleted");
      await fetchSavedSearches();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete");
    }
  };

  const activeFilterCount = [
    filters.q, filters.direction !== "all", filters.start_date, filters.end_date,
    filters.tier !== "all", filters.owner !== "all", filters.domain, filters.company, filters.matched_only
  ].filter(Boolean).length;

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="email-search-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 font-['Outfit']">Email Search</h1>
        <p className="text-slate-500 mt-1">Search across all synced emails. Replaces the imprecise Outlook search.</p>
      </div>

      {/* Saved Searches strip */}
      {savedSearches.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="saved-searches-strip">
          <Bookmark className="h-4 w-4 text-slate-400" />
          <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">Saved Searches:</span>
          {savedSearches.map((saved) => (
            <button
              key={saved.id}
              type="button"
              onClick={() => handleLoadSavedSearch(saved)}
              className="group flex items-center gap-1 px-3 py-1 bg-white border border-slate-200 hover:border-[#2E7AD1] hover:bg-blue-50 rounded-full text-xs transition-colors"
              title={saved.user_email === user.email ? "Your saved search" : `Shared by ${saved.user_email}`}
              data-testid={`saved-search-${saved.id}`}
            >
              <span className="text-slate-700">{saved.name}</span>
              {saved.shared && <span className="text-[10px] text-slate-400">·shared</span>}
              {saved.user_email === user.email && (
                <Trash2
                  className="h-3 w-3 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => handleDeleteSavedSearch(saved.id, e)}
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Search bar + filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder="Search subject, sender, recipient, content..."
                className="pl-10"
                data-testid="email-search-input"
              />
            </div>
            <Button onClick={runSearch} disabled={loading} className="bg-[#2E7AD1] hover:bg-[#2563EB]" data-testid="run-search-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
              Search
            </Button>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4 mr-1" /> Filters {activeFilterCount > 0 && <Badge variant="secondary" className="ml-2">{activeFilterCount}</Badge>}
            </Button>
            <Button variant="outline" onClick={() => setShowSaveDialog(!showSaveDialog)} disabled={activeFilterCount === 0} data-testid="save-search-btn">
              <BookmarkPlus className="h-4 w-4 mr-1" /> Save
            </Button>
          </div>

          {/* Save dialog */}
          {showSaveDialog && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2" data-testid="save-search-dialog">
              <Label className="text-xs font-semibold">Save current filters as:</Label>
              <div className="flex gap-2">
                <Input
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveSearch()}
                  placeholder='e.g. "Gold tier responses last 7 days"'
                  className="flex-1"
                  data-testid="save-name-input"
                />
                <label className="flex items-center gap-1 text-xs whitespace-nowrap px-2">
                  <input
                    type="checkbox"
                    checked={saveShared}
                    onChange={(e) => setSaveShared(e.target.checked)}
                    className="rounded"
                  />
                  Share with team
                </label>
                <Button size="sm" onClick={handleSaveSearch} disabled={saving || !saveName.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-save-btn">
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bookmark className="h-3 w-3" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-slate-100">
              <div>
                <Label className="text-xs">Direction</Label>
                <Select value={filters.direction} onValueChange={(v) => setFilters(f => ({ ...f, direction: v }))}>
                  <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                    <SelectItem value="outbound">Outbound</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">From Date</Label>
                <Input type="date" value={filters.start_date} onChange={(e) => setFilters(f => ({ ...f, start_date: e.target.value }))} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="text-xs">To Date</Label>
                <Input type="date" value={filters.end_date} onChange={(e) => setFilters(f => ({ ...f, end_date: e.target.value }))} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="text-xs">Domain (e.g. biogen.com)</Label>
                <Input value={filters.domain} onChange={(e) => setFilters(f => ({ ...f, domain: e.target.value }))} placeholder="biogen.com" className="mt-1 h-9" />
              </div>
              <div>
                <Label className="text-xs">Company</Label>
                <Input value={filters.company} onChange={(e) => setFilters(f => ({ ...f, company: e.target.value }))} placeholder="exact company name" className="mt-1 h-9" />
              </div>
              <div>
                <Label className="text-xs">Lead Tier</Label>
                <Select value={filters.tier} onValueChange={(v) => setFilters(f => ({ ...f, tier: v }))}>
                  <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="Gold">Gold</SelectItem>
                    <SelectItem value="Silver">Silver</SelectItem>
                    <SelectItem value="Bronze">Bronze</SelectItem>
                    <SelectItem value="Unranked">Unranked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Owner</Label>
                <Select value={filters.owner} onValueChange={(v) => setFilters(f => ({ ...f, owner: v }))}>
                  <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Owners</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 text-sm pb-2">
                  <input
                    type="checkbox"
                    checked={filters.matched_only}
                    onChange={(e) => setFilters(f => ({ ...f, matched_only: e.target.checked }))}
                    className="rounded"
                  />
                  Matched only
                </label>
              </div>
              <div className="md:col-span-4 flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={clearFilters}><X className="h-3 w-3 mr-1" /> Clear all</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Inbox className="h-5 w-5 text-slate-400" />
                Results
                {results.total_matching > 0 && <Badge variant="secondary">{results.messages.length} of {results.total_matching}</Badge>}
              </CardTitle>
              <CardDescription>{loading ? "Searching..." : results.total_matching > 0 ? "Click any email to expand and view full body" : "Run a search to see results"}</CardDescription>
            </div>
            {results.messages.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleAiSummary} disabled={summarizing} data-testid="ai-summary-btn">
                {summarizing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                AI Summarize Top 30
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {aiSummary && (
            <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-lg">
              <p className="text-xs font-semibold text-violet-700 mb-1">AI Summary of Top {Math.min(results.messages.length, 30)} Results</p>
              <p className="text-sm text-violet-900 whitespace-pre-wrap">{aiSummary}</p>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Searching emails...
            </div>
          ) : results.messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Mail className="h-10 w-10 mb-2 opacity-50" />
              <p className="text-sm">{activeFilterCount > 0 ? "No emails match your filters" : "Enter a search query and click Search"}</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-[700px] overflow-y-auto" data-testid="email-search-results">
              {results.messages.map((m) => (
                <EmailMessageRow key={m.id} message={m} query={filters.q} navigate={navigate} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
